import { ContentDialog } from '@spark-web/modal-dialog';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Footer } from '~/components/footer';
import { RequestQuoteForm } from '~/components/form/request-quote-form';
import { AnalyticsEvents } from '~/constants/analytics';
import { useModal } from '~/hooks/useModal';
import { LDFeatureFlag } from '~/types/feature-flag';
import { CustomerFormValues } from '~/types/marketplace';

import { useLeadCreationFetcher } from './hooks/useLeadCreationFetcher';
import { LandingInformation } from './landing-information';

type SelectedVendorProps = {
  firstVendorAdded?: boolean;
  onRequestQuotes: () => void;
};

export const SelectedVendor = ({ firstVendorAdded, onRequestQuotes }: SelectedVendorProps) => {
  const { open, close, onCloseAutoFocus, isOpen } = useModal('selected-vendor');
  const { open: openLanding, close: closeLanding } = useModal('landing-information');

  const { onSubmit, isLoading, error, lead, postcode, selectedCategories, track } = useLeadCreationFetcher({
    onRequestQuotes,
    close,
    closeLanding,
  });

  const { bypassContactFormToEnterMarketplace } = useFlags<LDFeatureFlag>();

  const isCustomerFormFieldRequired = !lead && postcode !== '' && (selectedCategories?.length ?? 0) > 0;

  useEffect(() => {
    // only show landing modal after user selected first vendor
    if (bypassContactFormToEnterMarketplace && firstVendorAdded) {
      openLanding();
    }
  }, [bypassContactFormToEnterMarketplace, openLanding, firstVendorAdded]);

  const formMethods = useForm<CustomerFormValues>({
    mode: 'all',
  });

  const onGetStarted = () => {
    closeLanding();

    // if customer form fields are required, open the form modal, otherwise open the quote request modal
    if (isCustomerFormFieldRequired) {
      open();
    } else {
      track(AnalyticsEvents.SelectedRequestQuotesClicked);
      onRequestQuotes();
    }
  };

  if (!bypassContactFormToEnterMarketplace) return null;

  return (
    <>
      <ContentDialog
        title="Brighte Marketplace"
        showCloseButton
        isOpen={isOpen}
        onToggle={close}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <FormProvider {...formMethods}>
          <RequestQuoteForm
            isLoading={isLoading}
            onSubmit={onSubmit}
            error={error}
            isAddVendorFlow
            leadType="Marketplace"
          />
          <Footer />
        </FormProvider>
      </ContentDialog>
      <LandingInformation onGetStarted={onGetStarted} />
    </>
  );
};
